import React, { useState, useEffect, useCallback, useRef } from "react";
import { useAuth } from "../contexts/AuthContext";
import { useEvent } from "../contexts/EventContext";
import { useSearchParams, useNavigate } from "react-router-dom";
import { useGroupNavigation } from "../hooks/useGroupNavigation";
import { apiService } from "../services/api";
import { useApiCall } from "../hooks/useApiCall";
import { Group, Person } from "../types";
import { SurpriseEventBanner, NoEventState } from "../components/events";
import {
  MagnifyingGlassIcon,
  NoteIcon,
  ConfettiIcon,
  CheckCircleIcon,
  CheckIcon,
} from "@phosphor-icons/react";
import "./pages.css";
import "./dashboard-header.css";

interface PersonWithStats extends Person {
  totalItems: number;
  reservedItems: number;
  hasMyReservations: boolean;
  isAvailable: boolean;
}

const DashboardPage: React.FC = () => {
  const { currentGroupSlug, isAuthenticated, getUserMainPerson } = useAuth();
  const { selectedEvent, events, selectEvent, isLoadingEvents, isInitialized } =
    useEvent();
  const [group, setGroup] = useState<
    (Group & { persons: Person[]; isAdmin: boolean }) | null
  >(null);
  const [personsWithStats, setPersonsWithStats] = useState<PersonWithStats[]>(
    []
  );
  const [isLoading, setIsLoading] = useState(true);
  const [searchParams, setSearchParams] = useSearchParams();
  const [showLoginSuccess, setShowLoginSuccess] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [userHasActiveList, setUserHasActiveList] = useState(false);
  const [mainPerson, setMainPerson] = useState<Person | null>(null);
  const [showSpinner, setShowSpinner] = useState(false);
  const [hasLoadedPersonsStats, setHasLoadedPersonsStats] = useState(false);

  const navigate = useNavigate();
  const { navigateToMyLists, getGroupPath } = useGroupNavigation();

  // Refs to prevent duplicate calls
  const loadingGroupRef = useRef(false);
  const loadingStatsRef = useRef(false);
  const lastEventIdRef = useRef<number | null>(null);

  // Optimized API call hooks
  const groupDetailsCall = useApiCall(apiService.getGroupDetails, { debounceMs: 100 });
  const eventStatsCall = useApiCall(apiService.getEventListStats, { debounceMs: 200 });
  const reservationsCall = useApiCall(
    (personId: number, eventId: number) => apiService.getMyReservationsForEvent(personId, eventId),
    { debounceMs: 100 }
  );

  useEffect(() => {
    // Check for login success parameter
    if (searchParams.get("login") === "success") {
      setShowLoginSuccess(true);
      // Clean up URL
      setSearchParams({});
      // Hide success message after 3 seconds
      setTimeout(() => setShowLoginSuccess(false), 3000);
    }

    // Check for event selection parameter
    const eventIdParam = searchParams.get("event");
    if (eventIdParam && events.length > 0) {
      const eventId = Number.parseInt(eventIdParam, 10);
      const eventExists = events.find((e) => e.id === eventId);
      if (eventExists) {
        selectEvent(eventId);
        // Clean up URL parameter
        const newParams = new URLSearchParams(searchParams);
        newParams.delete("event");
        setSearchParams(newParams);
      }
    }
  }, [searchParams, setSearchParams, events, selectEvent]);

  const loadGroupData = useCallback(async () => {
    if (!currentGroupSlug || loadingGroupRef.current) return;

    try {
      loadingGroupRef.current = true;
      setIsLoading(true);
      const groupData = await groupDetailsCall.execute(currentGroupSlug);
      setGroup(groupData);
    } catch (error) {
      console.error("Failed to load group data:", error);
    } finally {
      setIsLoading(false);
      loadingGroupRef.current = false;
    }
  }, [currentGroupSlug, groupDetailsCall]);

  const loadPersonsStatsForEvent = useCallback(async (persons: Person[]) => {
    if (!selectedEvent) {
      setPersonsWithStats([]);
      setHasLoadedPersonsStats(false);
      return;
    }

    // Skip if already loading or if it's the same event
    if (loadingStatsRef.current || lastEventIdRef.current === selectedEvent.id) {
      return;
    }

    try {
      loadingStatsRef.current = true;
      lastEventIdRef.current = selectedEvent.id;
      setIsLoading(true);
      setHasLoadedPersonsStats(false); // Reset when starting to load new data

      // Get event-specific stats for all persons
      const eventStatsResponse = await eventStatsCall.execute(selectedEvent.id);
      const eventStats = eventStatsResponse.stats;
      const mainPersonHasList = eventStatsResponse.mainPersonHasList;

      // Load my reservations for the main person only
      let reservations: any[] = [];
      let currentMainPerson: Person | null = null;
      try {
        currentMainPerson = await getUserMainPerson();
        setMainPerson(currentMainPerson);
        if (currentMainPerson) {
          reservations = await reservationsCall.execute(currentMainPerson.id, selectedEvent.id);
        }
      } catch (error) {
        console.error("Error loading my reservations:", error);
      }

      // Check if the current user has an active list for this event
      setUserHasActiveList(mainPersonHasList);

      // Create a set of person IDs for whom I have reservations
      const personsWithMyReservations = new Set(
        reservations.map((reservation: any) => reservation.listItem.person.id)
      );

      // Filter persons who have active lists for this event (backend will handle filtering)
      const personsWithActiveLists = persons.filter(
        (person) => eventStats[person.id] !== undefined
      );

      const results = personsWithActiveLists.map((person) => ({
        ...person,
        totalItems: eventStats[person.id]?.totalItems || 0,
        reservedItems: eventStats[person.id]?.reservedItems || 0,
        hasMyReservations: personsWithMyReservations.has(person.id),
        isAvailable: eventStats[person.id]?.isAvailable ?? true,
      }));

      setPersonsWithStats(results);
      setHasLoadedPersonsStats(true);
    } catch (error) {
      console.error("Error loading event stats:", error);
      setPersonsWithStats([]);
      setHasLoadedPersonsStats(true); // Mark as loaded even on error
    } finally {
      setIsLoading(false);
      loadingStatsRef.current = false;
    }
  }, [selectedEvent, getUserMainPerson, eventStatsCall, reservationsCall]);

  useEffect(() => {
    if (isAuthenticated && currentGroupSlug) {
      loadGroupData();
    }
  }, [isAuthenticated, currentGroupSlug, loadGroupData]);

  useEffect(() => {
    if (group && selectedEvent) {
      loadPersonsStatsForEvent(group.persons);
    }
  }, [group, selectedEvent, loadPersonsStatsForEvent]);

  // Delay spinner to avoid flash for fast loads
  useEffect(() => {
    let timer: NodeJS.Timeout;

    if (isLoading || !isInitialized) {
      // Show spinner after 500ms delay
      timer = setTimeout(() => {
        setShowSpinner(true);
      }, 500);
    } else {
      // Hide spinner immediately when loading is done
      setShowSpinner(false);
    }

    return () => {
      if (timer) clearTimeout(timer);
    };
  }, [isLoading, isInitialized]);

  if ((isLoading || !isInitialized) && showSpinner) {
    return (
      <div className="dashboard-page">
        {/* <div className="loading-container"></div> */}
      </div>
    );
  }

  return (
    <div className="dashboard-page">
      {showLoginSuccess && (
        <div className="login-success-banner">
          <div className="success-message">
            <CheckCircleIcon size={20} weight="fill" />
            Accesso effettuato con successo! Benvenuto in CosaVorrei.
          </div>
        </div>
      )}

      <div className="dashboard-container">
        {/* Header con evento selezionato */}
        <div className="dashboard-header">
          <div className="header-main">
            <h1>{group?.name}</h1>
            {selectedEvent && (
              <div className="selected-event-info">
                <h2>{selectedEvent.name}</h2>
                <span className="event-date">
                  {new Date(selectedEvent.eventDate).toLocaleDateString(
                    "it-IT",
                    {
                      weekday: "long",
                      year: "numeric",
                      month: "long",
                      day: "numeric",
                    }
                  )}
                </span>
              </div>
            )}
          </div>
        </div>

        {/* Testo introduttivo */}
        <div className="intro-section">
          {selectedEvent ? (
            <>
              {personsWithStats.length === 0 &&
              userHasActiveList &&
              mainPerson ? (
                <>
                  <p className="intro-text">
                    Questo evento è tutto per te, scegli cosa vuoi ricevere per{" "}
                    <strong>{selectedEvent.name}</strong>.
                  </p>
                  <div className="single-user-action">
                    <button
                      className="view-button my-list-button"
                      onClick={() => navigateToMyLists()}
                    >
                      VAI ALLA MIA LISTA
                    </button>
                  </div>
                </>
              ) : (
                <>
                  <p className="intro-text">
                    Clicca su una persona per vedere la sua lista regalo per
                    l'evento <strong>{selectedEvent.name}</strong> e prenotare i
                    regali che vuoi fare.
                  </p>
                  {userHasActiveList && !selectedEvent.isSurprise && (
                    <div className="intro-text intro-subtext">
                      <button
                        onClick={() => navigateToMyLists()}
                        className="intro-link"
                        style={{
                          background: "none",
                          border: "none",
                          padding: 0,
                          font: "inherit",
                          cursor: "pointer",
                        }}
                      >
                        Questo evento è anche per te! <br />
                        Hai già fatto la lista per questo evento?
                      </button>
                    </div>
                  )}
                </>
              )}
            </>
          ) : events.length > 0 ? (
            <p className="intro-text">
              Seleziona un evento per vedere le liste regalo disponibili.
            </p>
          ) : null}
        </div>

        {/* Surprise Event Banner */}
        {selectedEvent?.isSurprise && mainPerson && (
          <SurpriseEventBanner
            event={selectedEvent}
            hasActiveList={userHasActiveList}
            isOnlyUser={personsWithStats.length === 0 && userHasActiveList}
          />
        )}

        {/* Barra di ricerca */}
        {personsWithStats.length > 3 &&
          !(
            personsWithStats.length === 0 &&
            userHasActiveList &&
            mainPerson
          ) && (
            <div className="search-section">
              <div className="search-box">
                <input
                  type="text"
                  placeholder="Cerca una persona..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="search-input"
                />
                <MagnifyingGlassIcon size={20} className="search-icon" />
              </div>
            </div>
          )}

        {/* Griglia delle persone (desktop) */}
        {!(
          personsWithStats.length === 0 &&
          userHasActiveList &&
          mainPerson
        ) && (
          <div
            className={`persons-grid ${
              personsWithStats.length <= 3 ? "centered" : ""
            }`}
          >
            {personsWithStats
              .filter((person) =>
                person.name.toLowerCase().includes(searchTerm.toLowerCase())
              )
              .map((person) => (
                <button
                  key={person.id}
                  className="person-card"
                  onClick={() =>
                    person.isAvailable &&
                    navigate(getGroupPath(`/browse-lists?person=${person.id}`))
                  }
                >
                  {person.hasMyReservations && (
                    <div
                      className="reservation-flag"
                      title="Hai prenotato un regalo per questa persona"
                    >
                      <CheckIcon size={14} weight="bold" />
                    </div>
                  )}
                  <div className="person-avatar">
                    <div className="avatar-circle">
                      {person.name.charAt(0).toUpperCase()}
                    </div>
                  </div>

                  <div className="person-info">
                    <h3>{person.name}</h3>
                    {person.parentPersonId && (
                      <span className="person-type">Bambino</span>
                    )}
                  </div>

                  <div className="person-stats">
                    {person.isAvailable ? (
                      <>
                        <div className="stat">
                          <span className="stat-number">
                            {person.totalItems}
                          </span>
                          <span className="stat-label">regali in lista</span>
                        </div>
                        <div className="stat">
                          <span className="stat-number reserved">
                            {person.reservedItems}
                          </span>
                          <span className="stat-label">già prenotati</span>
                        </div>
                      </>
                    ) : (
                      <div className="stat-placeholder">
                        <span className="status-message">
                          Questa persona sta ancora preparando la sua lista,
                          torna più tardi.
                        </span>
                      </div>
                    )}
                  </div>

                  <div className="person-actions">
                    {person.isAvailable ? (
                      <span className="view-button">Vedi lista</span>
                    ) : (
                      <span className="view-button disabled">
                        Lista in preparazione
                      </span>
                    )}
                  </div>
                </button>
              ))}
          </div>
        )}

        {/* Lista delle persone (mobile) */}
        {!(
          personsWithStats.length === 0 &&
          userHasActiveList &&
          mainPerson
        ) && (
          <div
            className={`persons-list ${
              personsWithStats.length <= 3 ? "centered" : ""
            }`}
          >
            {personsWithStats
              .filter((person) =>
                person.name.toLowerCase().includes(searchTerm.toLowerCase())
              )
              .map((person) => (
                <button
                  key={`list-${person.id}`}
                  className="person-card list-style"
                  onClick={() =>
                    person.isAvailable &&
                    navigate(getGroupPath(`/browse-lists?person=${person.id}`))
                  }
                >
                  {person.hasMyReservations && (
                    <div
                      className="reservation-flag"
                      title="Hai prenotato un regalo per questa persona"
                    >
                      <CheckIcon size={12} weight="bold" />
                    </div>
                  )}
                  {/* Sezione sinistra: Avatar + Info */}
                  <div className="person-left">
                    <div className="person-avatar">
                      <div className="avatar-circle">
                        {person.name.charAt(0).toUpperCase()}
                      </div>
                    </div>

                    <div className="person-info">
                      <div className="person-name-row">
                        <h3>{person.name}</h3>
                        {person.parentPersonId && (
                          <span className="person-type">Bambino</span>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Sezione destra: Stats + Azioni */}
                  <div className="person-right">
                      {person.isAvailable && (
                        <div className="person-stats">
                          <div className="stat">
                            <span className="stat-number">
                              {person.totalItems}
                            </span>
                            <span className="stat-label">regali</span>
                          </div>
                          <div className="stat">
                            <span className="stat-number reserved">
                              {person.reservedItems}
                            </span>
                            <span className="stat-label">prenotati</span>
                          </div>
                        </div>
                      )}

                    <div className="person-actions">
                      {person.isAvailable ? (
                        <span className="view-button">Vedi lista</span>
                      ) : (
                        <span className="view-button disabled">
                          Lista in preparazione
                        </span>
                      )}
                    </div>
                  </div>
                </button>
              ))}
          </div>
        )}

        {personsWithStats.length === 0 &&
          selectedEvent &&
          hasLoadedPersonsStats &&
          !(userHasActiveList && mainPerson) && (
            <div className="empty-state">
              <div className="empty-icon">
                <NoteIcon size={48} weight="light" />
              </div>
              <h3>Nessuna lista attiva</h3>
              <p>
                Non ci sono ancora liste regalo attive per{" "}
                <strong>{selectedEvent.name}</strong>.
              </p>
              <p>
                Le persone devono essere attivate dall'amministratore per poter
                creare liste per questo evento.
              </p>
            </div>
          )}

        {!selectedEvent && isInitialized && (
          <>
            {/* Show different message based on whether there are events and user role */}
            {group?.isAdmin && group.persons.length <= 1 ? (
              // Admin with no other members - prioritize adding people first
              <>
                <h3>Aggiungi i primi membri della famiglia</h3>
                <p>
                  Prima di creare eventi, aggiungi le persone che parteciperanno
                  al gruppo.
                </p>
                <button
                  className="create-event-button"
                  onClick={() => navigate(getGroupPath("/admin#members"))}
                >
                  <ConfettiIcon size={16} weight="fill" />
                  Aggiungi membri
                </button>
              </>
            ) : events.length === 0 && !isLoadingEvents ? (
              // No events exist but there are members (and events have finished loading)
              group?.isAdmin ? (
                <>
                  <h3>Crea il primo evento per la tua famiglia</h3>
                  <p>
                    Ora che hai aggiunto i membri, crea un evento per iniziare a
                    organizzare i regali!
                  </p>
                  <button
                    className="create-event-button"
                    onClick={() => navigate(getGroupPath("/admin#events"))}
                  >
                    <ConfettiIcon size={16} weight="fill" />
                    Crea il primo evento
                  </button>
                </>
              ) : (
                <>
                  <h3>Non ci sono ancora eventi per la tua famiglia</h3>
                  <p>Attendi che l'amministratore del gruppo ne crei uno.</p>
                </>
              )
            ) : (
              <NoEventState
                isAdmin={Boolean(group?.isAdmin)}
                hasExistingEvents={events.length > 0}
                onCreateEvent={
                  group?.isAdmin
                    ? () => navigate(getGroupPath("/admin#events"))
                    : undefined
                }
              />
            )}
          </>
        )}

        {personsWithStats.length > 0 &&
          personsWithStats.filter((person) =>
            person.name.toLowerCase().includes(searchTerm.toLowerCase())
          ).length === 0 && (
            <div className="empty-state">
              <p>Nessuna persona trovata per "{searchTerm}"</p>
            </div>
          )}
      </div>
    </div>
  );
};

export default DashboardPage;
